import React, { useState, useEffect } from 'react';
import api, { enfantService, getMediaUrl } from '../services/api';
import logger from '../utils/logger';

const InformationsEnfantForm = ({ inscriptionId, onSuccess, readOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inscription, setInscription] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showConsentDetails, setShowConsentDetails] = useState(false);

  const [formData, setFormData] = useState({
    // Responsable légal
    responsable_nom: '',
    responsable_prenom: '',
    responsable_lien: '',
    responsable_adresse: '',
    responsable_code_postal: '',
    responsable_ville: '',
    responsable_telephone: '',
    responsable_email: '',
    numero_securite_sociale: '',
    compagnie_assurance: '',
    numero_contrat_assurance: '',

    // Volet social
    numero_msa_caf: '',
    partenaire_affilie: '',
    travailleur_social_referent: '',

    // Contact d'urgence
    urgence_nom_prenom: '',
    urgence_lien: '',
    urgence_telephone: '',

    // Médecin traitant
    medecin_nom: '',
    medecin_prenom: '',
    medecin_telephone: '',

    // Régime alimentaire
    regime_alimentaire: 'AUCUN',
    regime_alimentaire_autre: '',

    // Remarques
    remarques: '',

    // RGPD - Consentement données de santé
    health_data_consent: false
  });

  useEffect(() => {
    loadInscription();
  }, [inscriptionId]);

  const loadInscription = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/inscriptions/${inscriptionId}/`);
      const data = response.data;

      setInscription(data);

      // Initialiser l'aperçu de la photo si elle existe
      if (data.enfant_details?.photo_url) {
        // Utiliser l'URL de l'endpoint protégé si disponible
        setPhotoPreview(data.enfant_details.photo_url);
      } else if (data.enfant_details?.photo) {
        // Fallback sur l'ancien système pour rétrocompatibilité
        setPhotoPreview(getMediaUrl(data.enfant_details.photo));
      }

      setFormData({
        responsable_nom: data.responsable_nom || '',
        responsable_prenom: data.responsable_prenom || '',
        responsable_lien: data.responsable_lien || '',
        responsable_adresse: data.responsable_adresse || '',
        responsable_code_postal: data.responsable_code_postal || '',
        responsable_ville: data.responsable_ville || '',
        responsable_telephone: data.responsable_telephone || '',
        responsable_email: data.responsable_email || '',
        numero_securite_sociale: data.numero_securite_sociale || '',
        compagnie_assurance: data.compagnie_assurance || '',
        numero_contrat_assurance: data.numero_contrat_assurance || '',
        numero_msa_caf: data.numero_msa_caf || '',
        partenaire_affilie: data.partenaire_affilie || '',
        travailleur_social_referent: data.travailleur_social_referent || '',
        urgence_nom_prenom: data.urgence_nom_prenom || '',
        urgence_lien: data.urgence_lien || '',
        urgence_telephone: data.urgence_telephone || '',
        medecin_nom: data.medecin_nom || '',
        medecin_prenom: data.medecin_prenom || '',
        medecin_telephone: data.medecin_telephone || '',
        regime_alimentaire: data.regime_alimentaire || 'AUCUN',
        regime_alimentaire_autre: data.regime_alimentaire_autre || '',
        remarques: data.remarques || '',
        health_data_consent: data.health_data_consent || false
      });
    } catch (err) {
      logger.error('Erreur chargement inscription:', err);
      setError('Erreur lors du chargement des informations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      // Créer un aperçu de la photo
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation : la photo est obligatoire
    if (!photoPreview) {
      setError('⚠️ La photo de l\'enfant est obligatoire pour valider l\'inscription');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validation : le consentement RGPD pour les données de santé est obligatoire
    if (!formData.health_data_consent) {
      setError('⚠️ Vous devez donner votre consentement pour le traitement des données de santé de votre enfant. Ce consentement est nécessaire à l\'exécution du contrat d\'inscription.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validation des champs obligatoires
    const requiredFields = [
      'responsable_nom', 'responsable_prenom', 'responsable_lien',
      'responsable_adresse', 'responsable_code_postal', 'responsable_ville',
      'responsable_telephone', 'responsable_email',
      'numero_securite_sociale', 'compagnie_assurance', 'numero_contrat_assurance',
      'urgence_nom_prenom', 'urgence_lien', 'urgence_telephone'
    ];

    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const numeroSecuriteSocialeDigits = formData.numero_securite_sociale.replace(/\s/g, '');
    if (numeroSecuriteSocialeDigits.length !== 15) {
      setError('Le numéro de sécurité sociale doit contenir exactement 15 chiffres');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSaving(true);

      // 1. Mettre à jour la photo de l'enfant si une nouvelle photo a été sélectionnée
      if (photoFile && inscription?.enfant) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        photoFormData.append('prenom', inscription.enfant_details.prenom);
        photoFormData.append('nom', inscription.enfant_details.nom);
        photoFormData.append('date_naissance', inscription.enfant_details.date_naissance);
        photoFormData.append('lieu_naissance', inscription.enfant_details.lieu_naissance);

        await enfantService.update(inscription.enfant, photoFormData);
      }

      // 2. Mettre à jour les informations de l'inscription via complete_infos
      await api.post(`/inscriptions/${inscriptionId}/complete_infos/`, {
        ...formData,
        numero_securite_sociale: numeroSecuriteSocialeDigits,
      });

      // 3. Recharger l'inscription pour obtenir les données mises à jour (notamment health_consent_date)
      await loadInscription();

      setSuccess('Informations enregistrées avec succès');
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Erreur sauvegarde:', err);
      setError('Erreur lors de l\'enregistrement des informations');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Section Photo de l'enfant */}
      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-blue-500">
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          Photo de l'enfant <span className="text-red-500">*</span>
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          La photo est obligatoire pour valider l'inscription
        </p>
        <div className="flex items-start gap-6">
          {photoPreview ? (
            <div className="flex-shrink-0">
              <img
                src={photoPreview}
                alt="Aperçu photo enfant"
                className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
              />
            </div>
          ) : (
            <div className="flex-shrink-0 w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="mt-1 text-xs text-gray-500">Aucune photo</p>
              </div>
            </div>
          )}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {photoPreview ? 'Changer la photo' : 'Ajouter une photo'} <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              disabled={readOnly}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2.5 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                transition-all
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-2 text-xs text-gray-500">
              Format accepté : JPG, PNG, JPEG. Taille max : 5 Mo
            </p>
          </div>
        </div>
      </div>

      {/* Section Responsable Légal */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Responsable légal de l'enfant
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="responsable_nom"
              value={formData.responsable_nom}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={100}
              pattern="[A-Za-zÀ-ÿ\s\-']+"
              title="Lettres, espaces, tirets et apostrophes uniquement"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prénom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="responsable_prenom"
              value={formData.responsable_prenom}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={100}
              pattern="[A-Za-zÀ-ÿ\s\-']+"
              title="Lettres, espaces, tirets et apostrophes uniquement"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien avec l'enfant <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="responsable_lien"
              value={formData.responsable_lien}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={50}
              placeholder="Père, Mère, Tuteur..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="responsable_adresse"
              value={formData.responsable_adresse}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={255}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code postal <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="responsable_code_postal"
              value={formData.responsable_code_postal}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={5}
              pattern="[0-9]{5}"
              title="Code postal français à 5 chiffres"
              placeholder="75000"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ville <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="responsable_ville"
              value={formData.responsable_ville}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={100}
              pattern="[A-Za-zÀ-ÿ\s\-']+"
              title="Lettres, espaces, tirets et apostrophes uniquement"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="responsable_telephone"
              value={formData.responsable_telephone}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={20}
              pattern="[0-9\s\.\-]+"
              title="Numéro de téléphone français (chiffres, espaces, points ou tirets)"
              placeholder="06 12 34 56 78"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="responsable_email"
              value={formData.responsable_email}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={254}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              N° de Sécurité Sociale <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="numero_securite_sociale"
              value={formData.numero_securite_sociale}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={21}
              pattern="[0-9\s]+"
              title="15 chiffres (espaces autorisés)"
              placeholder="1 23 45 67 890 123 45"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Compagnie d'assurance <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="compagnie_assurance"
              value={formData.compagnie_assurance}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={200}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              N° de contrat <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="numero_contrat_assurance"
              value={formData.numero_contrat_assurance}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={50}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Section Volet Social */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Volet social (optionnel)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numéro de MSA/CAF
            </label>
            {/* Message informatif sur l'importance du numéro CAF */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3 rounded">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-800">
                  Sans ce numéro, nous ne pourrons pas vérifier votre éligibilité aux aides de la CAF
                </p>
              </div>
            </div>
            <input
              type="text"
              name="numero_msa_caf"
              value={formData.numero_msa_caf}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={50}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Affilié à un partenaire
            </label>
            <input
              type="text"
              name="partenaire_affilie"
              value={formData.partenaire_affilie}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={200}
              placeholder="Nom du partenaire"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Travailleur social référent
            </label>
            <input
              type="text"
              name="travailleur_social_referent"
              value={formData.travailleur_social_referent}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={200}
              placeholder="Nom du travailleur social"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Section Contact d'urgence */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          En cas d'urgence
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom et prénom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="urgence_nom_prenom"
              value={formData.urgence_nom_prenom}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={200}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien avec l'enfant <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="urgence_lien"
              value={formData.urgence_lien}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={100}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="urgence_telephone"
              value={formData.urgence_telephone}
              onChange={handleChange}
              disabled={readOnly}
              required
              maxLength={20}
              pattern="[0-9\s\.\-]+"
              title="Numéro de téléphone français (chiffres, espaces, points ou tirets)"
              placeholder="06 12 34 56 78"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Médecin traitant - Nom
            </label>
            <input
              type="text"
              name="medecin_nom"
              value={formData.medecin_nom}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={100}
              pattern="[A-Za-zÀ-ÿ\s\-']+"
              title="Lettres, espaces, tirets et apostrophes uniquement"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Médecin traitant - Prénom
            </label>
            <input
              type="text"
              name="medecin_prenom"
              value={formData.medecin_prenom}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={100}
              pattern="[A-Za-zÀ-ÿ\s\-']+"
              title="Lettres, espaces, tirets et apostrophes uniquement"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Médecin traitant - Téléphone
            </label>
            <input
              type="tel"
              name="medecin_telephone"
              value={formData.medecin_telephone}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={20}
              pattern="[0-9\s\.\-]+"
              title="Numéro de téléphone français (chiffres, espaces, points ou tirets)"
              placeholder="05 12 34 56 78"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Section Régime alimentaire */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Régime alimentaire spécifique
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Régime alimentaire
            </label>
            <select
              name="regime_alimentaire"
              value={formData.regime_alimentaire}
              onChange={handleChange}
              disabled={readOnly}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="AUCUN">Aucun</option>
              <option value="SANS_PORC">Sans porc</option>
              <option value="VEGETARIEN">Végétarien</option>
              <option value="VEGAN">Végan</option>
              <option value="AUTRE">Autre</option>
            </select>
          </div>
          {formData.regime_alimentaire === 'AUTRE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Préciser
              </label>
              <input
                type="text"
                name="regime_alimentaire_autre"
                value={formData.regime_alimentaire_autre}
                onChange={handleChange}
                disabled={readOnly}
                maxLength={200}
                placeholder="Précisez le régime alimentaire"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Section Remarques */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Remarques
        </h3>
        <textarea
          name="remarques"
          value={formData.remarques}
          onChange={handleChange}
          disabled={readOnly}
          rows="4"
          maxLength={1000}
          placeholder="Informations complémentaires que vous souhaitez partager..."
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          {formData.remarques.length}/1000 caractères
        </p>
      </div>

      {/* Section RGPD - Consentement données de santé */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg rounded-lg p-3 sm:p-6 border-l-4 border-blue-600">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-4 flex items-center">
          <svg className="w-4 h-4 sm:w-6 sm:h-6 mr-1 sm:mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span className="text-xs sm:text-base">Protection des données de santé (RGPD)</span>
        </h3>

        {/* Consentement déjà donné - Non modifiable */}
        {formData.health_data_consent && inscription?.health_consent_date && (
          <div className="bg-green-50 border-l-4 border-green-400 p-2 sm:p-4 mb-2 sm:mb-4 rounded">
            <div className="flex items-start">
              <svg className="h-4 w-4 sm:h-6 sm:w-6 text-green-600 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-green-800 mb-1">Consentement enregistré</p>
                <p className="text-xs sm:text-sm text-green-700 mb-1 sm:mb-2">
                  Vous avez donné votre consentement pour le traitement des données de santé de votre enfant
                  le {new Date(inscription.health_consent_date).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}.
                </p>
                <p className="text-xs sm:text-sm text-green-700">
                  Pour modifier, contactez-nous à{' '}
                  <a href="mailto:cdccolonies@gmail.com" className="font-semibold underline hover:text-green-900">
                    cdccolonies@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire de consentement */}
        {(!formData.health_data_consent || !inscription?.health_consent_date) && (
          <>
            <div className="flex items-start bg-white rounded-lg p-2 sm:p-4 border-2 border-blue-300 mb-2 sm:mb-3">
              <input
                type="checkbox"
                id="health_data_consent"
                name="health_data_consent"
                checked={formData.health_data_consent}
                onChange={handleChange}
                disabled={readOnly || (formData.health_data_consent && inscription?.health_consent_date)}
                className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              />
              <label htmlFor="health_data_consent" className="ml-2 sm:ml-3 text-xs sm:text-sm">
                <span className="font-semibold text-gray-900">
                  Je consens au traitement des données de santé de mon enfant <span className="text-red-500">*</span>
                </span>
                <p className="text-gray-700 mt-1 leading-snug">
                  Je comprends que ces informations sont nécessaires pour assurer la sécurité et le bien-être
                  de mon enfant pendant le séjour. Je donne mon consentement explicite pour que l'organisation
                  collecte et traite les données de santé de mon enfant (médecin traitant, régime alimentaire,
                  remarques médicales) conformément à l'article 9 du RGPD. Ce consentement est valable uniquement
                  pour cette inscription. Un nouveau consentement vous sera demandé pour chaque nouvelle inscription.
                </p>
              </label>
            </div>

            {/* Bouton pour afficher/masquer les détails */}
            <button
              type="button"
              onClick={() => setShowConsentDetails(!showConsentDetails)}
              className="w-full text-left bg-white rounded-lg p-2 sm:p-3 border border-blue-200 hover:bg-blue-50 transition-colors flex items-center justify-between mb-2 sm:mb-3"
            >
              <span className="text-xs sm:text-sm font-medium text-blue-700">
                {showConsentDetails ? 'Masquer les détails' : 'Afficher les détails sur la protection de vos données'}
              </span>
              <svg
                className={`w-4 h-4 sm:w-5 sm:h-5 text-blue-600 transition-transform flex-shrink-0 ${showConsentDetails ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Détails dépliables */}
            {showConsentDetails && (
              <div className="space-y-2 sm:space-y-3 bg-white rounded-lg p-2 sm:p-4 border border-blue-200">
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-2">Pourquoi collectons-nous ces données ?</h4>
                  <p className="text-xs sm:text-sm text-gray-700 mb-1 sm:mb-2 leading-snug">
                    Pour assurer la <strong>sécurité et le bien-être</strong> de votre enfant pendant le séjour,
                    nous devons collecter des informations de santé (régime alimentaire, médecin traitant, remarques médicales).
                  </p>
                  <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                    Ces données sont des <strong>informations sensibles</strong> protégées par l'article 9 du RGPD
                    et nécessitent votre <strong>consentement explicite</strong>.
                  </p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 sm:p-3 rounded">
                  <p className="text-xs sm:text-sm font-semibold text-yellow-800 mb-1">Important :</p>
                  <p className="text-xs sm:text-sm text-yellow-800 leading-snug">
                    Ce consentement est <strong>nécessaire à l'exécution du contrat d'inscription</strong>.
                    Sans ces informations, nous ne pourrons malheureusement pas garantir la sécurité de votre enfant
                    et nous ne serons donc pas en mesure de traiter l'inscription.
                  </p>
                </div>

                <div>
                  <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                    <strong>Vos droits :</strong> Vous disposez d'un droit d'accès, de rectification, d'opposition,
                    de limitation et de portabilité de vos données. Ces données seront conservées pendant 1 an après
                    le séjour puis supprimées (sauf obligations légales comptables).
                  </p>
                </div>
              </div>
            )}

            {/* Lien vers la page RGPD complète */}
            <div className="mt-2 sm:mt-3">
              <a
                href="/politique-rgpd-sante"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium inline-flex items-center"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">Consulter notre politique complète de protection des données de santé</span>
                <span className="sm:hidden">Politique complète RGPD santé</span>
                <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Message d'avertissement si pas coché */}
            {!formData.health_data_consent && (
              <div className="mt-2 sm:mt-3 bg-red-50 border border-red-200 rounded p-2 sm:p-3">
                <p className="text-xs sm:text-sm text-red-700">
                  <strong>Attention :</strong> Le consentement est obligatoire pour valider l'inscription.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {!readOnly && (
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
          >
            {saving ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </span>
            ) : (
              'Enregistrer les informations'
            )}
          </button>
        </div>
      )}
    </form>
  );
};

export default InformationsEnfantForm;
